/**
 * /app/$tenantSlug/shifts/swaps — Shift Swaps.
 *
 * Two-stage swap workflow surface: create a swap request, the named
 * counterparty accepts (-> pending_manager), then an approver who is NOT a
 * party (the requester's manager, else the next approver up / HR) approves
 * (-> swaps both roster rows). All authorization is server-enforced; this
 * page shows every swap the caller can see and the action available to
 * them per status (lib/swap-actions.ts).
 *
 * 2026-10 QA (#2 / #6): the counterparty is picked from the directory
 * (not a raw employee id), both parties are shown by name, a party is
 * never offered Approve / Reject (the counterparty can only accept,
 * decline or withdraw their acceptance; the requester can only cancel),
 * and server messages ("counterparty_id must be another employee", "you
 * are the other employee in this swap …") are shown instead of generic
 * "Failed to …" toasts.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { EmployeeDirectoryPicker } from "@/components/shared/employee-directory-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { toApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { decisionToast } from "@/lib/approval-copy";
import { swapActionsFor } from "@/lib/swap-actions";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { useDirectory } from "@/hooks/use-directory";
import {
  useSwaps,
  useCreateSwap,
  useAcceptSwap,
  useApproveSwap,
  useRejectSwap,
  useCancelSwap,
} from "@/hooks/use-shifts";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/shifts/swaps",
)({
  component: SwapsRoute,
});

/**
 * The reject / decline / withdraw / cancel confirm — every one closes the
 * swap. `cancel` (the requester calling off their own request) takes no
 * reason, so the dialog hides the note field for it.
 */
type CloseMode = "reject" | "decline" | "withdraw" | "cancel";

const CLOSE_COPY: Record<
  CloseMode,
  { title: string; body: string; confirm: string; reasonRequired: boolean; hasNote: boolean }
> = {
  reject: {
    title: "Reject shift-swap request?",
    body: "This rejects the swap request and notifies both employees. A reason is required so they understand the decision.",
    confirm: "Reject",
    reasonRequired: true,
    hasNote: true,
  },
  decline: {
    title: "Decline this swap?",
    body: "The swap request is closed as declined. Your colleague can raise a new one.",
    confirm: "Decline swap",
    reasonRequired: false,
    hasNote: true,
  },
  withdraw: {
    title: "Withdraw your acceptance?",
    body: "The swap is closed before anyone approves it and your colleague is told. They can raise a new one.",
    confirm: "Withdraw",
    reasonRequired: false,
    hasNote: true,
  },
  cancel: {
    title: "Cancel this swap request?",
    body: "The swap request is withdrawn and your colleague is told. You can raise a new one later.",
    confirm: "Cancel request",
    reasonRequired: false,
    hasNote: false,
  },
};

function SwapsRoute() {
  const { user } = useAuth();
  const myId = user?.user_id;
  const { isApprover } = useLeaveRoles();
  const swaps = useSwaps();
  const dir = useDirectory({ includeInactive: true });
  const create = useCreateSwap();
  const accept = useAcceptSwap();
  const approve = useApproveSwap();
  const reject = useRejectSwap();
  const cancel = useCancelSwap();

  const [reqDate, setReqDate] = useState("");
  const [cpId, setCpId] = useState<number | undefined>();
  const [cpDate, setCpDate] = useState("");
  const [note, setNote] = useState("");
  // Remounts the picker after a successful request so it shows the
  // placeholder again (a controlled Radix Select going back to
  // `undefined` keeps displaying the last pick).
  const [formKey, setFormKey] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);

  // Reject / decline / withdraw / cancel dialog. null id == closed.
  const [closing, setClosing] = useState<{ id: number; mode: CloseMode } | null>(null);
  const [closeReason, setCloseReason] = useState("");

  async function act(p: Promise<unknown>, ok: string) {
    try {
      await p;
      toast.success(ok);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  /** "Asha Menon and Ravi Kumar" — both employees of a swap. */
  const parties = (s: { requester_id: number; counterparty_id: number }) =>
    `${dir.label(s.requester_id, null, false)} and ${dir.label(s.counterparty_id, null, false)}`;

  async function confirmClose() {
    if (!closing) return;
    const trimmed = closeReason.trim();
    if (CLOSE_COPY[closing.mode].reasonRequired && !trimmed) return;
    const row = rows.find((s) => s.id === closing.id);
    if (closing.mode === "cancel") {
      try {
        await cancel.mutateAsync(closing.id);
        toast.success("Swap request cancelled");
        setClosing(null);
        setCloseReason("");
      } catch (err) {
        toast.error(toApiError(err).message);
      }
      return;
    }
    try {
      await reject.mutateAsync({
        id: closing.id,
        body: trimmed ? { decision_notes: trimmed } : undefined,
      });
      toast.success(
        closing.mode === "reject"
          ? decisionToast("swap", "rejected", row ? parties(row) : "Both employees", true)
          : closing.mode === "decline"
            ? "Swap declined — your colleague has been notified."
            : "Acceptance withdrawn — the swap is closed and your colleague and the approver have been notified.",
      );
      setClosing(null);
      setCloseReason("");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function submit() {
    setFormError(null);
    if (!reqDate || !cpDate || !cpId) {
      setFormError("Pick your date, the colleague and their date.");
      return;
    }
    try {
      await create.mutateAsync({
        requester_date: reqDate,
        counterparty_id: cpId,
        counterparty_date: cpDate,
        note: note.trim() || null,
      });
      toast.success(`Swap requested — ${dir.label(cpId)} has been asked to accept it.`);
      setReqDate("");
      setCpId(undefined);
      setCpDate("");
      setNote("");
      setFormKey((k) => k + 1);
    } catch (err) {
      setFormError(toApiError(err).message);
    }
  }

  const rows = swaps.data?.swaps ?? [];
  const closeCopy = closing ? CLOSE_COPY[closing.mode] : null;
  const closePending = reject.isPending || cancel.isPending;

  return (
    <TimeAndLeaveShell
      title="Shift Swaps"
      description="Request a swap, accept one, or approve as a manager."
    >
      <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Request a Swap</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="rd">My Date</Label>
              <Input id="rd" type="date" value={reqDate} onChange={(e) => setReqDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Swap With</Label>
              <EmployeeDirectoryPicker
                key={formKey}
                value={cpId}
                onChange={setCpId}
                excludeId={myId}
                placeholder="Choose a colleague"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cd">Their Date</Label>
              <Input id="cd" type="date" value={cpDate} onChange={(e) => setCpDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="note">Note (Optional)</Label>
            <Textarea id="note" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          </div>
          {formError && (
            <Alert variant="destructive">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <Button onClick={() => void submit()} disabled={create.isPending}>
            Request Swap
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Swaps</CardTitle>
        </CardHeader>
        <CardContent>
          {swaps.isPending && (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}
          {swaps.isError && (
            <Alert variant="destructive">
              <AlertTitle>Could not load swaps</AlertTitle>
              <AlertDescription>{toApiError(swaps.error).message}</AlertDescription>
            </Alert>
          )}
          {swaps.data && rows.length === 0 && <EmptyState title="No swaps yet." />}
          {rows.length > 0 && (
            <ul className="divide-y text-sm">
              {rows.map((s) => {
                const actions = swapActionsFor(s, myId, isApprover);
                return (
                  <li key={s.id} className="space-y-1 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        <span className="font-medium">{dir.label(s.requester_id)}</span>{" "}
                        ({formatDate(s.requester_date)}) ↔{" "}
                        <span className="font-medium">{dir.label(s.counterparty_id)}</span>{" "}
                        ({formatDate(s.counterparty_date)})
                      </span>
                      <StatusBadge status={s.status} />
                    </div>
                    {s.note && <p className="text-muted-foreground">“{s.note}”</p>}
                    {actions.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {actions.includes("accept") && (
                          <Button
                            size="sm"
                            disabled={accept.isPending}
                            onClick={() =>
                              void act(accept.mutateAsync(s.id), "Accepted — sent for approval")
                            }
                          >
                            Accept
                          </Button>
                        )}
                        {actions.includes("approve") && (
                          <Button
                            size="sm"
                            disabled={approve.isPending}
                            onClick={() =>
                              void act(
                                approve.mutateAsync({ id: s.id }),
                                decisionToast("swap", "approved", parties(s), true),
                              )
                            }
                          >
                            Approve
                          </Button>
                        )}
                        {(["reject", "decline", "withdraw"] as const)
                          .filter((m) => actions.includes(m))
                          .map((m) => (
                            <Button
                              key={m}
                              size="sm"
                              variant="outline"
                              disabled={reject.isPending}
                              onClick={() => {
                                setCloseReason("");
                                setClosing({ id: s.id, mode: m });
                              }}
                            >
                              {m === "reject" ? "Reject" : m === "decline" ? "Decline" : "Withdraw Acceptance"}
                            </Button>
                          ))}
                        {actions.includes("cancel") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={cancel.isPending}
                            onClick={() => setClosing({ id: s.id, mode: "cancel" })}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
      </div>

      <AlertDialog
        open={closing !== null}
        onOpenChange={(o) => {
          if (!o && !closePending) setClosing(null);
        }}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{closeCopy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{closeCopy?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          {closeCopy?.hasNote && (
          <div className="space-y-2">
            <Label htmlFor="swap-route-close-reason">
              {closeCopy?.reasonRequired ? "Reason (Required)" : "Note (Optional)"}
            </Label>
            <Textarea
              id="swap-route-close-reason"
              rows={3}
              value={closeReason}
              aria-required={closeCopy?.reasonRequired}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder={
                closeCopy?.reasonRequired
                  ? "Why is this swap being rejected?"
                  : "Optional — shared with your colleague."
              }
            />
          </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closePending}>Keep It</AlertDialogCancel>
            <AlertDialogAction
              // Keep the dialog open while the mutation runs; confirmClose
              // closes it on success. Default Action auto-closes on click.
              onClick={(e) => {
                e.preventDefault();
                void confirmClose();
              }}
              disabled={
                closePending ||
                (closeCopy?.reasonRequired === true && !closeReason.trim())
              }
              className={cn(buttonVariants({ variant: "destructive" }))}
            >
              {closeCopy?.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TimeAndLeaveShell>
  );
}
